import axios from '../axios';
import { AxiosHeaders } from 'axios';
import type { AxiosResponse } from 'axios';

import type { Playlist } from '../interfaces/playlists';
import type { Category } from '../interfaces/categories';
import type { Pagination, PaginationQueryParams } from '../interfaces/api';

const BROWSE_PLAYLISTS_ENABLED =
  process.env.REACT_APP_ENABLE_SPOTIFY_BROWSE_PLAYLISTS === 'true';

const emptyPlaylistPage = (params: PaginationQueryParams = {}): Pagination<Playlist> => ({
  href: '',
  items: [],
  limit: params.limit ?? 0,
  offset: params.offset ?? 0,
  previous: null,
  next: null,
  total: 0,
});

/**
 * @description Get a list of categories used to tag items in Spotify (on, for example, the Spotify player’s “Browse” tab).
 */
const fetchCategories = (params: PaginationQueryParams = {}) =>
  axios.get<{ categories: Pagination<Category> }>('/browse/categories', { params });

/**
 * @description Get a list of Spotify playlists tagged with a particular category.
 */
const fetchCategoryPlaylists = (categoryId: string, params: PaginationQueryParams = {}) =>
  BROWSE_PLAYLISTS_ENABLED
    ? axios.get<{ playlists: Pagination<Playlist> }>(
        `/browse/categories/${categoryId}/playlists`,
        { params }
      )
    : Promise.resolve<AxiosResponse<{ playlists: Pagination<Playlist> }>>({
        data: { playlists: emptyPlaylistPage(params) },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {
          url: `/browse/categories/${categoryId}/playlists`,
          params,
          headers: new AxiosHeaders(),
        },
      });

/**
 * @description Get a single category used to tag items in Spotify (on, for example, the Spotify player’s “Browse” tab).
 */
const fetchCategory = (categoryId: string) =>
  axios.get<Category>(`/browse/categories/${categoryId}`);

export const categoriesService = {
  fetchCategories,
  fetchCategoryPlaylists,
  fetchCategory,
};
